import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtErr } from "../lib/api";
import { Modal, Field, ModalFooter } from "./Contacts";
import { ArrowLeft, Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { StatusBadge } from "../components/StatusBadge";

export default function ListDetailPage() {
  const { id } = useParams();
  const [lst, setLst] = useState(null);
  const [openAdd, setOpenAdd] = useState(false);

  const load = () => api.getList(id).then(setLst);
  useEffect(() => { load(); }, [id]);

  if (!lst) return <div className="text-sm text-zinc-500">Loading…</div>;

  const remove = async (cid) => { await api.listRemove(id, [cid]); toast.success("Removed"); load(); };

  return (
    <div className="space-y-4">
      <Link to="/lists" className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-950"><ArrowLeft size={14}/> All lists</Link>
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-heading text-2xl font-medium tracking-tight">{lst.name}</h2>
          <p className="text-sm text-zinc-500 mt-0.5">{(lst.members||[]).length} members</p>
        </div>
        <button data-testid="add-members" onClick={() => setOpenAdd(true)} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-zinc-950 text-white hover:bg-zinc-800"><Plus size={14}/> Add contacts</button>
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-xs text-zinc-500 border-b border-zinc-200">
            <tr><th className="text-left px-3 py-2 font-medium">Name</th><th className="text-left px-3 py-2 font-medium">Email</th><th className="text-left px-3 py-2 font-medium">Stage</th><th className="text-right px-3 py-2"/></tr>
          </thead>
          <tbody>
            {(lst.members || []).map(m => (
              <tr key={m.id} className="border-b border-zinc-100 hover:bg-zinc-50/60">
                <td className="px-3 py-2"><Link to={`/contacts/${m.id}`} className="font-medium hover:underline">{m.first_name} {m.last_name}</Link></td>
                <td className="px-3 py-2 font-mono text-[12.5px] text-zinc-700">{m.email}</td>
                <td className="px-3 py-2"><StatusBadge value={m.lifecycle_stage}/></td>
                <td className="px-3 py-2 text-right"><button onClick={() => remove(m.id)} className="text-zinc-400 hover:text-rose-700"><Trash size={14}/></button></td>
              </tr>
            ))}
            {(!lst.members || lst.members.length === 0) && <tr><td colSpan={4} className="p-8 text-center text-sm text-zinc-500">No members yet</td></tr>}
          </tbody>
        </table>
      </div>

      {openAdd && <AddMembersModal listId={id} onClose={() => setOpenAdd(false)} onDone={() => { setOpenAdd(false); load(); }}/>}
    </div>
  );
}

function AddMembersModal({ listId, onClose, onDone }) {
  const [mode, setMode] = useState("filters");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(new Set());

  useEffect(() => {
    if (mode !== "pick") return;
    const t = setTimeout(() => api.listContacts({ q, page: 1, page_size: 30 }).then(d => setItems(d.items)), 250);
    return () => clearTimeout(t);
  }, [q, mode]);

  const submit = async () => {
    await api.listAdd(listId, Array.from(sel));
    toast.success(`Added ${sel.size} contacts`);
    onDone();
  };

  const [f, setF] = useState({ q: "", stage: "", status: "", tier: "", company: "", role: "", level: "", org: "", job_title: "", has_linkedin: "", tag: "" });
  const [opts, setOpts] = useState({ roles: [], levels: [], orgs: [] });
  const [tagOpts, setTagOpts] = useState([]);
  const [preview, setPreview] = useState(null);
  const [adding, setAdding] = useState(false);
  useEffect(() => { api.contactFilterOptions().then(setOpts).catch(() => {}); }, []);
  useEffect(() => { api.listTags().then(d => setTagOpts(d.items || [])).catch(() => {}); }, []);
  const activeParams = useMemo(() => Object.fromEntries(Object.entries(f).filter(([, v]) => v)), [f]);
  const anyF = Object.keys(activeParams).length > 0;
  useEffect(() => {
    if (!anyF) { setPreview(null); return; }
    const t = setTimeout(() => api.listContacts({ ...activeParams, page: 1, page_size: 6 }).then(setPreview).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [activeParams, anyF]);

  const set = (k) => (e) => setF(prev => ({ ...prev, [k]: e.target.value }));
  const addAll = async () => {
    setAdding(true);
    try {
      const r = await api.listAddByFilter(listId, activeParams);
      toast.success(`Added ${r.added} contact${r.added === 1 ? "" : "s"}${r.already_in_list ? ` — ${r.already_in_list} already in the list` : ""}`);
      onDone();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setAdding(false);
    }
  };

  const inputCls = "h-8 rounded-md border border-zinc-200 px-2 text-sm";
  return (
    <Modal title="Add contacts to list" onClose={onClose} wide>
      <div className="flex gap-1.5 mb-3">
        <button data-testid="lam-mode-filters" onClick={() => setMode("filters")} className={`text-xs px-2.5 py-1.5 rounded-md border ${mode === "filters" ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-200 text-zinc-600 hover:border-zinc-300"}`}>By filters</button>
        <button data-testid="lam-mode-pick" onClick={() => setMode("pick")} className={`text-xs px-2.5 py-1.5 rounded-md border ${mode === "pick" ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-200 text-zinc-600 hover:border-zinc-300"}`}>Pick individually</button>
      </div>

      {mode === "filters" && (
        <>
          <div className="flex flex-wrap gap-2">
            <input data-testid="lam-q" value={f.q} onChange={set("q")} placeholder="Search name, email, company…" className={`${inputCls} flex-1 min-w-[180px]`} />
            <input data-testid="lam-company" value={f.company} onChange={set("company")} placeholder="Company…" className={`${inputCls} w-36`} />
            <input data-testid="lam-jobtitle" value={f.job_title} onChange={set("job_title")} placeholder="Job title contains… (comma = OR, e.g. trial, clinical research)" className={`${inputCls} w-72`} />
            <select data-testid="lam-role" value={f.role} onChange={set("role")} className={`${inputCls} max-w-[170px]`}>
              <option value="">All roles</option>
              {opts.roles.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <select data-testid="lam-level" value={f.level} onChange={set("level")} className={inputCls}>
              <option value="">All levels</option>
              {opts.levels.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
            <select data-testid="lam-org" value={f.org} onChange={set("org")} className={`${inputCls} max-w-[150px]`}>
              <option value="">All org types</option>
              {opts.orgs.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            <select data-testid="lam-stage" value={f.stage} onChange={set("stage")} className={inputCls}>
              <option value="">All stages</option>
              <option value="lead">Lead</option>
              <option value="qualified">Qualified</option>
              <option value="opportunity">Opportunity</option>
              <option value="customer">Customer</option>
            </select>
            <select data-testid="lam-status" value={f.status} onChange={set("status")} className={inputCls}>
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="left_company">No longer at company</option>
              <option value="unsubscribed">Unsubscribed</option>
              <option value="bounced">Bounced</option>
            </select>
            <select data-testid="lam-tier" value={f.tier} onChange={set("tier")} className={inputCls}>
              <option value="">All tiers</option>
              <option value="hot">🔥 Hot (15+)</option>
              <option value="warm">Warm (5–15)</option>
              <option value="cool">Cool (0–5)</option>
              <option value="cold">Cold (0)</option>
            </select>
            <select data-testid="lam-linkedin" value={f.has_linkedin} onChange={set("has_linkedin")} className={inputCls}>
              <option value="">LinkedIn: any</option>
              <option value="yes">Has LinkedIn</option>
              <option value="no">No LinkedIn</option>
              <option value="review">LinkedIn: needs review</option>
            </select>
            <select data-testid="lam-tag" value={f.tag} onChange={set("tag")} className={`${inputCls} max-w-[160px]`}>
              <option value="">All tags</option>
              {tagOpts.map(t => <option key={t.name} value={t.name}>{t.name}{t.count ? ` (${t.count})` : ""}</option>)}
            </select>
          </div>

          <div className="mt-3 border border-zinc-200 rounded-md p-3 bg-zinc-50/60 min-h-[80px]">
            {!anyF && <p className="text-sm text-zinc-400">Pick at least one filter to see who would be added.</p>}
            {anyF && !preview && <p className="text-sm text-zinc-400">Counting…</p>}
            {anyF && preview && (
              <>
                <p className="text-sm text-zinc-700" data-testid="lam-preview-count"><span className="font-medium">{Number(preview.total).toLocaleString()}</span> contact{preview.total === 1 ? "" : "s"} match your filters</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {preview.items.map(c => (
                    <span key={c.id} className="text-[11px] px-1.5 py-0.5 rounded bg-white border border-zinc-200 text-zinc-600">{c.first_name} {c.last_name} · {c.company}</span>
                  ))}
                  {preview.total > preview.items.length && <span className="text-[11px] text-zinc-400">+ {Number(preview.total - preview.items.length).toLocaleString()} more</span>}
                </div>
              </>
            )}
          </div>

          <ModalFooter onClose={onClose}>
            <button data-testid="lam-add-all" disabled={!anyF || !preview || preview.total === 0 || adding} onClick={addAll} className="modal-btn-primary">
              {adding ? "Adding…" : `Add all ${preview ? Number(preview.total).toLocaleString() : ""} matching`}
            </button>
          </ModalFooter>
        </>
      )}

      {mode === "pick" && (
        <>
          <input data-testid="add-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="modal-input mb-3"/>
          <div className="max-h-80 overflow-auto border border-zinc-200 rounded-md">
            {items.map(c => (
              <label key={c.id} className="flex items-center gap-2 px-3 py-2 border-b border-zinc-100 last:border-b-0 cursor-pointer hover:bg-zinc-50">
                <input type="checkbox" checked={sel.has(c.id)} onChange={() => { const n = new Set(sel); n.has(c.id) ? n.delete(c.id) : n.add(c.id); setSel(n); }}/>
                <span className="text-sm">{c.first_name} {c.last_name}</span>
                <span className="text-xs text-zinc-500 ml-auto">{c.email}</span>
              </label>
            ))}
          </div>
          <ModalFooter onClose={onClose}>
            <button data-testid="add-confirm" disabled={sel.size===0} onClick={submit} className="modal-btn-primary">Add {sel.size}</button>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
}
